import React, { useContext, useEffect, useState } from "react";
import SharedDataContext from "../../../SharedDataContext";
import Input from "../../Tags/Input";
import { Cross1Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import Image from "../../Tags/Image";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import TableContact from "./TableContact";
import { crmBulkSearch, getCompanyContactList } from "../../../apis/workspace/general";
import { paginationConfig } from "../../../axios/constant";
import { isEmptyObject, returnArray } from "../../../utils/GenericFn";
import useUpdateQuery from "../../../hooks/useUpdateQuery";
import Filter from "../../Filter";
import EditColumn from "../../EditColumn";
import { useMutation, useQuery } from "react-query";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import PrintDate from "../../Tags/PrintDate";
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon } from "lucide-react";
import { getSavedCRMConfig } from "../../../apis/workspace/integrations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import IcpPersona from "../../containers/Scoring/IcpPersona";
import { Skeleton } from "../../ui/skeleton";
import { useQueryParams } from "../../../hooks/useQueryParams";
import CRMStatus from "./CRMStatus";
import GenericModal from "../../GenericModal";
import useModal from "../../GenericModal/useModal";
import RevealContact from "./RevealContact";
import CRM from "../../ext/crm";

export default function ContactListOfCompany() {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const crmConfigId =
    activeWorkspace?.preferences && activeWorkspace?.preferences?.active_crm_workspace_integration_config_id;

  const getInitials = (name = "") => {
    if (!name) return "";

    const words = name.trim().split(/\s+/); // split by spaces
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // get company contact list

  const [apiResponse, setApiResponse] = useState({});
  const [contacts, setContacts] = useState([]);

  const [filterQuery, setFilterQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState(paginationConfig);
  const [sort, setSort] = useState("");

  const query = useQueryParams();

  const getCompanyContactListFn = useQuery(
    "get_company_list",
    () => {
      return getCompanyContactList({
        query: filterQuery,
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      });
    },
    {
      enabled: !!activeWorkspace,
      refetchOnWindowFocus: false,
      manual: true,
      onSuccess: (data) => {
        setApiResponse(data);
        setContacts(data?.data);
        let crmBulkSearchPayload = [];
        if (data?.data && data?.data.length > 0) {
          data?.data.forEach((item) => {
            crmBulkSearchPayload.push({
              record_id: item?.id,
              type: "person",
              company_record_id: "g3VFYpUB1ejGaXif3Ryg",
              edit_search: false,
            });
          });
          crmBulkSearchFn.mutate({
            data: crmBulkSearchPayload,
            configId: crmConfigId,
            options: {
              headers: { "X-Workspace-Id": activeWorkspace?.id },
            },
          });
        }
      },
      onError: (error) => {
        console.error(error);
      },
    }
  );

  const handleGetCompanyContactList = () => {
    getCompanyContactListFn.refetch();
  };

  useEffect(() => {
    setFilterQuery(generateQueryString());
  }, [pagination, filters, sort]);

  useEffect(() => {
    if (activeWorkspace?.id) {
      handleGetCompanyContactList();
    }
  }, [activeWorkspace, filterQuery]);

  const generateQueryString = (forApi = true) => {
    let params = [];
    if (forApi) {
      params = [`page=${pagination.page}`, `per_page=${pagination.perPage}`, `sort=${sort}`];
    }
    for (let key in filters) {
      let item = filters[key];
      if (item.selectedScope && item.value) {
        let parsedValue = item.value
          .split(",")
          .map((v) => v.trim())
          .join("|");
        params.push(`filter[${item.selectedScope}]=${parsedValue}`);
      }
    }

    return params.join("&");
  };

  const getFiltersObject = (data) => {
    if (isEmptyObject(data)) {
      setSort("");
    } else {
      setPagination(paginationConfig);
    }
    setFilters(data);
  };
  const getPageNumber = (page) => {
    //console.log(page);
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSetRole = (column) => {
    if (sort === `-${column}`) {
      setSort(column);
    } else if (sort === column) {
      setSort(`-${column}`);
    } else {
      setSort(column);
    }
  };
  const [columns, setColumns] = useState({});
  const [sortedVisibleColumns, setSortedVisibleColumns] = useState(null);

  const getColumns = (columns) => {
    //console.log(columns);
    setColumns(columns);
    const visibleColumns = Object.values(columns).filter((column) => column.visible);
    const sortedVisibleColumns = visibleColumns.sort((a, b) => a.order - b.order);
    setSortedVisibleColumns(sortedVisibleColumns);
  };

  const setQueryString = useUpdateQuery();
  useEffect(() => {
    if (!isEmptyObject(filters)) {
      setQueryString(generateQueryString(false));
    }
  }, [filters]);

  // get crm object

  const [crmObjectsConfig, setCRMObjectsConfig] = useState([]);
  const [savedConfig, setSavedConfig] = useState([]);
  const [personArr, setPersonArr] = useState([]);

  // get crm object list
  const getSavedCRMConfigFn = useMutation("get_saved_crm_object", getSavedCRMConfig, {
    onSuccess(data, variables, context) {
      // console.log("savedConfig", data);
      if (data && data.data && data.data.length > 0) {
        setCRMObjectsConfig(data?.data);
        const arrPeople = data?.data.filter((obj) => obj.type === "person");
        setSavedConfig(data.data);
        // console.log("arrPeople", arrPeople);
        setPersonArr(arrPeople);
      }
    },
  });

  const handleGetSavedCRMConfig = () => {
    getSavedCRMConfigFn.mutate({
      crmConfigId: crmConfigId,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };
  useEffect(() => {
    if (activeWorkspace && activeWorkspace.id) {
      handleGetSavedCRMConfig();
    }
  }, [activeWorkspace]);

  // check all items
  const [checkedItems, setCheckedItems] = useState([]);
  const toggleCheckbox = (contact) => {
    // setCheckedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    setCheckedItems((prev) => {
      const exists = prev.some((item) => item.id === contact.id);

      if (exists) {
        return prev.filter((item) => item.id !== contact.id);
      } else {
        return [...prev, contact];
      }
    });
  };

  // Toggle all
  const toggleAllCheckboxes = (isChecked, data) => {
    if (isChecked) {
      setCheckedItems(
        data.map((item) => ({
          id: item.id,
          isRevealed: item.isRevealed ?? false, // default to false if undefined
        }))
      );
    } else {
      setCheckedItems([]);
    }
  };

  useEffect(() => {
    console.log("checkedItems", checkedItems);
  }, [checkedItems]);

  const navigate = useNavigate();

  const [rowObject, setRowObject] = useState({});
  const updateRowObject = (key, value) => {
    setRowObject((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...value,
      },
    }));
  };

  const [personaError, setPersonaError] = useState(false);
  const [personaObject, setPersonaObject] = useState({});
  const updatePersonaObject = (key, value) => {
    setPersonaObject((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    console.log("personaObject", personaObject);
  }, [personaObject]);

  // check crm bulk search
  const [crmBulkSearchData, setCrmBulkSearchData] = useState([]);
  const crmBulkSearchFn = useMutation(crmBulkSearch, {
    onSuccess(data, variables, context) {
      console.log("✅ Raw CRM Bulk Search Data:", data);

      if (typeof data !== "string") {
        console.error("❌ Unexpected Data Format:", data);
        return;
      }

      // ✅ Step 1: Extract multiple `data:` JSON chunks
      const eventStrings = data.split("\n").filter((line) => line.startsWith("data:"));

      // ✅ Step 2: Parse each JSON object correctly
      let allRecords = {};
      eventStrings.forEach((event) => {
        try {
          const jsonString = event.replace("data: ", "").trim();
          const jsonData = JSON.parse(jsonString);

          const { recordId, data: records } = jsonData;

          if (!allRecords[recordId]) {
            allRecords[recordId] = [];
          }
          allRecords[recordId].push(...records);
        } catch (error) {
          console.error("❌ Error Parsing Event Data:", error);
        }
      });

      // ✅ Step 3: Convert the structured object into an array
      const formattedData = Object.entries(allRecords).map(([recordId, records]) => ({
        recordId,
        records,
      }));

      console.log("✅ Processed Formatted Data:", formattedData);
      setCrmBulkSearchData(formattedData);
    },

    onError(error) {
      console.error("❌ CRM Bulk Search Error:", error);
    },
  });

  const { isOpen, openModal, closeModal } = useModal();

  const [iframePayload, setIframePayload] = useState("");

  const handleSetIframePayload = (data) => {
    console.log("handleSetIframePayload", data);
    const payload = btoa(
      JSON.stringify({
        hasCompany: true,
        url: data?.profile_url,
        contact_id: data.id,
        company_url: data?.company_url,
      })
    );
    setIframePayload(payload);
  };

  const generateRandomString = () => {
    var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    var length = 40;
    var randomString = "";

    for (var i = 0; i < length; i++) {
      var randomIndex = Math.floor(Math.random() * characters.length);
      randomString += characters.charAt(randomIndex);
    }

    return randomString;
  };

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? "-";
  };

  const [isAnyRowFetchingRevealDetails, setIsAnyRowFetchingRevealDetails] = useState(true);

  function hasAnyFetchingRevealDetails(obj) {
    return Object.values(obj).some((item) => item.fetchingRevealDetails === true);
  }

  useEffect(() => {
    console.log("rowObject", rowObject);
    if (!isEmptyObject(rowObject)) {
      console.log("isAnyRowFetchingRevealDetails", hasAnyFetchingRevealDetails(rowObject));
      setIsAnyRowFetchingRevealDetails(hasAnyFetchingRevealDetails(rowObject));
    }
  }, [rowObject]);

  return (
    <div className="py-4">
      {/* <div className="flex">
        <div className="w-[400px]">
          <Input
            icon={<MagnifyingGlassIcon className="text-gray-400" />}
            placeholder="Search"
            className="h-9"
          />
        </div>
      </div> */}
      {apiResponse && apiResponse.data && (apiResponse.data.length > 0 || !isEmptyObject(filters)) && (
        <div className="flex justify-between items-center">
          <div className="space-x-3 inline-flex items-center">
            {apiResponse && apiResponse.filterMetadata && (
              <>
                <span>Filters:</span>
                <Filter filterMetadata={apiResponse.filterMetadata} getFiltersObject={getFiltersObject} />
              </>
            )}
          </div>
          <div className="space-x-3">
            {apiResponse.columnMetadata && (
              <EditColumn columnMetadata={apiResponse.columnMetadata} getColumns={getColumns} />
            )}
          </div>
        </div>
      )}

      {personArr && personArr.length > 0 && getCompanyContactListFn.isSuccess && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="mt-5 inline-flex focus:outline-none"
            disabled={checkedItems?.length === 0 ? true : false}
          >
            <Button
              variant="outline"
              className={`inline-flex items-center text-sm ${checkedItems?.length === 0 && "opacity-70"}`}
            >
              <Image src="/images/logos/integrations/crm.svg" width={24} className="no-invert mr-2" />
              <span>Add to CRM</span>
              <ChevronDownIcon className="ml-2" width={18} height={18} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent className="w-[230px]" align="start">
              <DropdownMenuGroup>
                {personArr.map((person) => (
                  <DropdownMenuItem
                    onClick={() => {
                      if (checkedItems.length === 0) {
                        toast.error("Please select contacts");
                        return;
                      }
                      console.log(person.crm_object_api_name);
                      navigate(
                        `/${activeWorkspace?.slug}/company-contact-list/push-to-crm?crm_object_api_name=${person.crm_object_api_name}&company=g3VFYpUB1ejGaXif3Ryg&contacts=${btoa(
                          JSON.stringify(checkedItems)
                        )}&callbackUrl=aHR0cHM6Ly93d3cubGlua2VkaW4uY29tL2NvbXBhbnkvbGFtYmRhdGVzdA==?tab=contacts`
                      );
                    }}
                  >
                    <div className="flex w-full py-1">
                      <Image src="/images/logos/integrations/crm.svg" width={24} className="no-invert mr-2" />
                      <span>Add to CRM</span>
                      <Badge variant="gray" className="ml-3">
                        {person?.crm_object_label}
                      </Badge>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>
      )}
      {apiResponse &&
        apiResponse.data &&
        apiResponse.data.length > 0 &&
        getCompanyContactListFn.isSuccess && (
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="border border-border rounded-[--radius]">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-gray-100 text-gray-700 font-semibold text-left">
                      <tr>
                        {returnArray(sortedVisibleColumns).map((column, index) => {
                          if (column?.key === "full_name") {
                            return (
                              <>
                                <th
                                  key={index}
                                  scope="col"
                                  className={`px-4 py-2 border-b border-r border-gray-200   ${
                                    column.sortable ? "cursor-pointer" : ""
                                  }`}
                                >
                                  <span className={"inline-flex items-center whitespace-pre"}>
                                    <input
                                      checked={contacts.length > 0 && checkedItems.length === contacts.length}
                                      onChange={(e) => toggleAllCheckboxes(e.target.checked, contacts)}
                                      type="checkbox"
                                      className="mr-3 h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-700 dark:text-brand-200"
                                      disabled={isAnyRowFetchingRevealDetails}
                                    />{" "}
                                    Name and title
                                    {column.sortable && (
                                      <div
                                        onClick={() => (column.sortable ? handleSetRole(column.key) : void 0)}
                                      >
                                        {sort === "" || (sort !== column.key && sort !== `-${column.key}`) ? (
                                          <Image
                                            src="/images/icons/dual-arrow.svg"
                                            width="7"
                                            height="7"
                                            className="inline-block ml-1"
                                          />
                                        ) : (
                                          <>
                                            {sort === column.key && (
                                              <ArrowDownIcon width={12} className="inline-block ml-1" />
                                            )}
                                            {sort === `-${column.key}` && (
                                              <ArrowUpIcon width={12} className="inline-block ml-1" />
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </span>
                                </th>
                              </>
                            );
                          }
                          if (column?.key === "current_job_title") {
                            return null;
                          } else
                            return (
                              <th
                                key={index}
                                scope="col"
                                className={`px-4 py-2 border-b border-r border-gray-200   ${
                                  column.sortable ? "cursor-pointer" : ""
                                }`}
                                onClick={() => (column.sortable ? handleSetRole(column.key) : void 0)}
                              >
                                <span className={"inline-flex items-center whitespace-pre"}>
                                  {column.display_name}
                                  {column.sortable && (
                                    <>
                                      {sort === "" || (sort !== column.key && sort !== `-${column.key}`) ? (
                                        <Image
                                          src="/images/icons/dual-arrow.svg"
                                          width="7"
                                          height="7"
                                          className="inline-block ml-1"
                                        />
                                      ) : (
                                        <>
                                          {sort === column.key && (
                                            <ArrowDownIcon width={12} className="inline-block ml-1" />
                                          )}
                                          {sort === `-${column.key}` && (
                                            <ArrowUpIcon width={12} className="inline-block ml-1" />
                                          )}
                                        </>
                                      )}
                                    </>
                                  )}
                                </span>
                              </th>
                            );
                        })}
                        <th className="px-4 py-2 border-b border-r border-gray-200">Persona fitment</th>
                        <th className="px-4 py-2 border-b border-r border-gray-200">Persona</th>
                        <th className="px-4 py-2 border-b border-r border-gray-200 whitespace-pre">
                          Contact details
                        </th>
                      </tr>
                      {/* <tr>
                        <th className="px-4 py-2 border-b border-r border-gray-200">
                          <input
                            checked={contacts.length > 0 && checkedItems.length === contacts.length}
                            onChange={(e) => toggleAllCheckboxes(e.target.checked, contacts)}
                            type="checkbox"
                            className="mr-3 h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-700 dark:text-brand-200"
                          />{" "}
                          Name and title
                        </th>
                        {personaError ? (
                          <>
                            <th className="px-4 py-2 border-b border-r border-gray-200">Company Name</th>
                            <th className="px-4 py-2 border-b border-r border-gray-200">
                              Company LinkedIn URL
                            </th>
                          </>
                        ) : (
                          <>
                            <th className="px-4 py-2 border-b border-r border-gray-200">Persona fitment</th>
                            <th className="px-4 py-2 border-b border-r border-gray-200">Persona</th>
                          </>
                        )}
                        <th className="px-4 py-2 border-b border-r border-gray-200">Contact details</th>
                      </tr> */}
                    </thead>
                    <tbody className="divide-y divide-border bg-background">
                      {contacts &&
                        returnArray(contacts).map((item, index) => (
                          <>
                            <tr
                              key={item.id}
                              className={`cursor-pointer text-paragraph ${index % 2 !== 0 ? "bg-smoke" : ""}`}
                            >
                              {returnArray(sortedVisibleColumns).map((column, colIndex) => {
                                const colKey =
                                  columns &&
                                  Object.keys(columns).find((key) => columns[key].order === column.order);

                                if (colKey === "full_name") {
                                  return (
                                    <td className="px-4 py-2 border-b border-r border-gray-200">
                                      <div className="flex items-center justify-between">
                                        <div className="inline-flex items-center mr-5">
                                          <input
                                            disabled={rowObject[item?.id]?.fetchingRevealDetails}
                                            checked={checkedItems.find((el) => el.id === item.id)}
                                            onChange={() => {
                                              toggleCheckbox({
                                                id: item?.id,
                                                isRevealed: rowObject[item?.id]?.isRevealed,
                                              });
                                            }}
                                            type="checkbox"
                                            className="mr-3 h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-700 dark:text-brand-200"
                                          />
                                          <div className="inline-flex items-center">
                                            <div className="h-9 w-9 flex-shrink-0 items-center bg-gray-100 border border-border justify-center inline-flex rounded-full text-xs">
                                              {getInitials(item.full_name)}
                                            </div>
                                            <div className="ml-4">
                                              <div className="font-bold text-gray-800 whitespace-pre">
                                                {item.full_name}
                                              </div>
                                              <div className="mt-0 text-gray-500 whitespace-pre">
                                                {item?.current_job_title}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        <CRM
                                          source="app"
                                          activeWorkspace={activeWorkspace}
                                          recordData={{
                                            name: item?.full_name,
                                            first_name: item?.first_name,
                                            last_name: item?.last_name,
                                            company: item?.current_company?.name,
                                            id: item?.id,
                                            linkedin_url: item?.social_media_urls_linkedin_url,
                                            company_linkedin_url:
                                              item?.current_company?.social_media_urls_linkedin_url,
                                          }}
                                          activeCrm={crmConfigId}
                                          type="person"
                                          crmObjectsConfig={crmObjectsConfig}
                                        />

                                        {crmBulkSearchFn?.isLoading ? (
                                          // <Button variant="outline" className="px-3 rounded-lg">
                                          //   <div className="loader_circle inline-block mr-2"></div>
                                          //   CRM
                                          // </Button>
                                          <></>
                                        ) : (
                                          <></>
                                          // <CRM
                                          //   activeWorkspace={activeWorkspace}
                                          //   recordData={item}
                                          //   activeCrm={crmConfigId}
                                          //   type="person"
                                          //   crmObjectsConfig={crmObjectsConfig}
                                          //   source="app"
                                          // />

                                          // <CRMStatus
                                          //   crmSearchData={
                                          //     crmBulkSearchData?.find((el) => el?.recordId === item?.id)
                                          //       ?.records || []
                                          //   }
                                          //   config={{
                                          //     workspaceId: activeWorkspace?.id,
                                          //     record_id: item?.id,
                                          //     activeCrmId: crmConfigId,
                                          //   }}
                                          //   openProfileIframe={() => {
                                          //     window.parent.postMessage(
                                          //       {
                                          //         type: "CONTACT_LIST_EVENT",
                                          //         payload: {
                                          //           url: item?.social_media_urls_linkedin_url,
                                          //           id: item.id,
                                          //           company_url:
                                          //             item?.current_company?.social_media_urls_linkedin_url,
                                          //         },
                                          //       },
                                          //       "*"
                                          //     );
                                          //   }}
                                          // />
                                        )}
                                      </div>
                                    </td>
                                  );
                                }
                                if (colKey === "current_job_title") {
                                  return null;
                                }
                                return (
                                  <td
                                    key={colIndex}
                                    className="px-4 py-2 border-b border-r border-gray-200 whitespace-pre"
                                  >
                                    {/* {colKey} */}
                                    {getNestedValue(item, colKey)}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 border-b border-r border-gray-200">
                                {/* <IcpPersona
                                  contactId={item.id}
                                  workspaceId={activeWorkspace.id}
                                  getPersona={(data) => {
                                    console.log("getPersona", data);
                                    updatePersonaObject(item.id, data?.personaName);
                                    setPersonaError(false);
                                  }}
                                  onError={() => {
                                    setPersonaError(true);
                                  }}
                                /> */}
                              </td>
                              <td className="px-4 py-2 border-b border-r border-gray-200">
                                {personaObject[item?.id] ? (
                                  <Badge variant="" className="whitespace-pre">
                                    {personaObject[item?.id]}
                                  </Badge>
                                ) : (
                                  <Skeleton className="w-24 rounded-xl h-10" />
                                )}
                              </td>
                              <td className="px-4 py-2 border-b border-gray-200">
                                <RevealContact
                                  contactId={item?.id}
                                  activeWorkspace={activeWorkspace}
                                  openProfileIframe={() => {
                                    window.parent.postMessage(
                                      {
                                        type: "CONTACT_LIST_EVENT",
                                        payload: {
                                          url: item?.social_media_urls_linkedin_url,
                                          id: item.id,
                                          company_url: item?.current_company?.social_media_urls_linkedin_url,
                                        },
                                      },
                                      "*"
                                    );
                                  }}
                                  getRevealedInfo={(status) => {
                                    updateRowObject(item?.id, { isRevealed: status });
                                  }}
                                  fetchingRevealDetails={(status) => {
                                    updateRowObject(item?.id, { fetchingRevealDetails: status });
                                  }}
                                />
                              </td>
                            </tr>
                          </>
                        ))}
                      {/* {contacts &&
                        returnArray(contacts).map((item, index) => {
                          return (
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-2 border-b border-r border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div className="inline-flex items-center">
                                    <input
                                      disabled={rowObject[item?.id]?.fetchingRevealDetails}
                                      checked={checkedItems.find((el) => el.id === item.id)}
                                      onChange={() => {
                                        toggleCheckbox({
                                          id: item?.id,
                                          isRevealed: rowObject[item?.id]?.isRevealed,
                                        });
                                      }}
                                      type="checkbox"
                                      className="mr-3 h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-700 dark:text-brand-200"
                                    />
                                    <div className="inline-flex items-center">
                                      <div className="h-9 w-9 flex-shrink-0 items-center bg-gray-100 border border-border justify-center inline-flex rounded-full text-xs">
                                        {getInitials(item.full_name)}
                                      </div>
                                      <div className="ml-4">
                                        <div className="font-bold text-gray-800">{item.full_name}</div>
                                        <div className="mt-0 text-gray-500">{item?.current_job_title}</div>
                                      </div>
                                    </div>
                                  </div>

                                  {crmBulkSearchFn?.isLoading ? (
                                    <Button variant="outline" className="px-3 rounded-lg">
                                      <div className="loader_circle inline-block mr-2"></div>
                                      CRM
                                    </Button>
                                  ) : (
                                    <CRMStatus
                                      crmSearchData={
                                        crmBulkSearchData?.find((el) => el?.recordId === item?.id)?.records ||
                                        []
                                      }
                                      config={{
                                        workspaceId: activeWorkspace?.id,
                                        record_id: item?.id,
                                        activeCrmId: crmConfigId,
                                      }}
                                      openProfileIframe={() => {
                                        window.parent.postMessage(
                                          {
                                            type: "CONTACT_LIST_EVENT",
                                            payload: {
                                              url: item?.social_media_urls_linkedin_url,
                                              id: item.id,
                                              company_url:
                                                item?.current_company?.social_media_urls_linkedin_url,
                                            },
                                          },
                                          "*"
                                        );
                                      }}
                                    />
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 border-b border-r border-gray-200">
                                {personaError ? (
                                  item?.current_company?.name
                                ) : (
                                  <IcpPersona
                                    contactId={item.id}
                                    workspaceId={activeWorkspace.id}
                                    getPersona={(data) => {
                                      console.log("getPersona", data);
                                      updatePersonaObject(item.id, data?.personaName);
                                      setPersonaError(false);
                                    }}
                                    onError={() => {
                                      setPersonaError(true);
                                    }}
                                  />
                                )}
                              </td>
                              <td className="px-4 py-2 border-b border-r border-gray-200">
                                {personaObject[item?.id] ? (
                                  <Badge variant="" className="whitespace-pre">
                                    {personaObject[item?.id]}
                                  </Badge>
                                ) : (
                                  <>
                                    {!personaError && <Skeleton className="h-8 w-36 rounded-xl" />}
                                    {personaError && item?.current_company?.social_media_urls_linkedin_url}
                                  </>
                                )}
                              </td>
                              <td className="px-4 py-2 border-b border-gray-200">
                                <RevealContact
                                  contactId={item?.id}
                                  activeWorkspace={activeWorkspace}
                                  openProfileIframe={() => {
                                    window.parent.postMessage(
                                      {
                                        type: "CONTACT_LIST_EVENT",
                                        payload: {
                                          url: item?.social_media_urls_linkedin_url,
                                          id: item.id,
                                          company_url: item?.current_company?.social_media_urls_linkedin_url,
                                        },
                                      },
                                      "*"
                                    );
                                  }}
                                  getRevealedInfo={(status) => {
                                    updateRowObject(item?.id, { isRevealed: status });
                                  }}
                                  fetchingRevealDetails={(status) => {
                                    updateRowObject(item?.id, { fetchingRevealDetails: status });
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })} */}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      {getCompanyContactListFn.isLoading && <TableLoader marginTop="mt-2" />}

      {getCompanyContactListFn.isSuccess &&
        apiResponse &&
        apiResponse.data &&
        apiResponse.data.length === 0 &&
        isEmptyObject(filters) && <NoData heading={"No contacts found!"} />}
      {getCompanyContactListFn.isSuccess &&
        apiResponse &&
        apiResponse.data &&
        apiResponse.data.length === 0 &&
        !isEmptyObject(filters) && (
          <div className="p-10 border rounded-lg mt-8">
            <NoData heading={"No keys found!"} filterMode={true} />
          </div>
        )}
      <div className="flex items-center justify-between  rounded-[--radius] py-3">
        <div className="flex-1">
          {apiResponse && apiResponse.pagination && apiResponse.pagination.total > 0 && (
            <Pagination data={apiResponse.pagination} getPageNumber={getPageNumber} />
          )}
        </div>
      </div>

      <GenericModal
        isOpen={isOpen}
        closeModal={closeModal}
        drawer={true}
        drawerWidth=" pros__iframe__wrapper__drawer"
      >
        <div id="pros__iframe__wrapper">
          <span
            id="pros__iframe__wrapper__hide"
            onClick={() => {
              setIframePayload("");
              closeModal();
            }}
            className="dark:text-gray-200"
          >
            <Cross1Icon width={12} height={12} />
          </span>
          <iframe
            src={`${process.env.REACT_APP_BASE_URL}/contact-viewer/?ref=${iframePayload}&source=app&refresh=${generateRandomString()}`}
          />
        </div>
      </GenericModal>
    </div>
  );
}
